import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { ModalShell } from "./ModalShell";
import { PrefCard, type PrefState } from "../Preferences/PrefCard";
import { useDevice } from "../../context/DeviceProvider";
import { useAuth } from "../../context/AuthProvider";
import { useToast } from "../../context/ToastProvider";
import {
  loadPreferences,
  savePreferences,
  saveRunningPace,
  type PreferenceOption,
  type PreferenceOptionCatalog,
  type SavePreferencesInput,
  type SerializedPreferences
} from "../../lib/preferencesApi";
import { getAnonRunningPaceDraft, setAnonRunningPaceDraft } from "../../lib/anonDraft";
import {
  RunningPaceField,
  RUNNING_PACE_HINT_PREFERENCES
} from "../Preferences/RunningPaceField";
import { useUnits } from "../../context/UnitsProvider";
import { isValidRunningPaceSecPerMi } from "@shared/modules/preferences/runningPace";
import {
  emptyCustomerProfile,
  loadProfile,
  overlayAnonPhysiologyOntoLoaded,
  physiologyFromInputs,
  saveProfile,
  setAnonPhysiologySession,
  type CustomerProfile
} from "../../lib/profileApi";
import type { Sex, WeightUnit } from "./PhysiologyFields";
import { TunePhysiologyCard } from "./TunePhysiologyCard";
import { getAnonPreferencesDraft, setAnonPreferencesDraft } from "../../lib/anonDraft";
import { UI_HIDDEN_FUEL_TYPE_KEYS } from "@shared/lib/preferenceOptions";
import { notifyPreferencesSaved } from "../../lib/preferencesSaved";
import { getActivePlanRegenerate } from "../../lib/planRegenerate";
import { patchFuelingProfile } from "../../lib/fuelingProfileApi";
import { FuelingSection, useFuelingProfileForm } from "../Preferences/FuelingSection";
import { useJourneys } from "../JourneyRail/useJourneys";
import { useOpenPlanSettings } from "../JourneyRail/useOpenPlanSettings";
import { journeyRaceName } from "../../lib/journeyApi";
import { ApiError, isAbortError } from "../../lib/api";
import { useModal } from "../../context/ModalProvider";
import "../Onboarding/onboarding.css";
import "./modals.css";
import { SegmentedControl, Spinner, UnitToggle } from "../ui";

/**
 * Preferences modal (journey §9, fixes B4). Rebuilds the old `/preferences`
 * route screen as a modal on the shared `ModalShell` — controlled tri-state
 * PrefCards (tap to cycle like → dislike → neutral) for flavors / fuel /
 * brands, allergen + dietary chip rows,
 * caffeine. Sex / weight reuse {@link TunePhysiologyCard} (same copy + layout as onboarding /
 * onboarding manual: 50/50 desktop columns). Budget section dropped (§9).
 *
 * Persistence (journey §9): customers save to the DB via `savePreferences`;
 * anonymous users have no DB row, so their edits are held in **`localStorage`**
 * (audit H5 — via `anonDraft.ts`, ~28-day TTL; legacy `sessionStorage` migrates once)
 */

type TriMap = Record<string, PrefState>;

// Fuel-type + dietary-style options this app deliberately hides from the
// Preferences modal (UI_HIDDEN_* in preferenceOptions). They're still used
// elsewhere, so we only suppress them from the UI — their stored state is
// hydrated into form state and re-emitted verbatim by `buildPayload`, so saving
// here never edits or clears them (and the server merges them back regardless).

function buildTriMap(
  options: PreferenceOption[],
  loved: string[],
  avoided: string[]
): TriMap {
  const m: TriMap = {};
  const loveSet = new Set(loved);
  const avoidSet = new Set(avoided);
  for (const o of options) {
    m[o.key] = loveSet.has(o.key) ? "love" : avoidSet.has(o.key) ? "avoid" : "neutral";
  }
  return m;
}

function splitTriMap(m: TriMap): { loved: string[]; avoided: string[] } {
  const loved: string[] = [];
  const avoided: string[] = [];
  for (const [k, v] of Object.entries(m)) {
    if (v === "love") loved.push(k);
    else if (v === "avoid") avoided.push(k);
  }
  return { loved, avoided };
}

export function PreferencesModal({
  onClose,
  onSaved,
  onRegenerate,
  initialSection,
  resumeView
}: {
  onClose: () => void;
  /** Fired after a successful save — lets callers refresh dependent views. */
  onSaved?: () => void;
  /**
   * Opened from a plan page (§ plan-detail): when provided AND the user has
   * changed something, the sub-view Save button splits into "Save & regenerate"
   * / "Save only". Fired after a successful save when the user picks regenerate.
   */
  onRegenerate?: () => void;
  /** Deep-link target — race-plan surfaces open straight at "Fueling"/"Taste" (R7). */
  initialSection?: "fueling" | "taste";
  /** Re-open at a sub-view WITH back-to-menu (unlike `initialSection` deep-links). */
  resumeView?: "fueling" | "taste";
}) {
  const device = useDevice();
  const isMobile = device === "mobile";
  // Regenerate handler for the Save split. Prefer an explicit prop; otherwise
  // fall back to the plan page's registered handler (so the global sub-nav /
  // deep-link openers offer regenerate while a plan is on screen). Frozen for
  // the modal's lifetime so it can't flip mid-edit.
  const [effectiveRegenerate] = useState(() => onRegenerate ?? getActivePlanRegenerate());
  /** Matched against `modals.css` caffeine row breakpoint (2-col ↔ stacked). */
  const [caffeineRowWide, setCaffeineRowWide] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 561px)").matches
      : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 561px)");
    const sync = () => setCaffeineRowWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const caffeineProductsLabelId = useId();
  const caffeineCapLabelId = useId();

  const { state: authState, refreshCsrf } = useAuth();
  const { push: pushToast } = useToast();

  // "Journey settings" opens a plan's settings modal DIRECTLY (My Training UX
  // fix §3) — the row used to blind-navigate to /plans, which stranded users on
  // the archive list. Only meaningful once ANY journey exists — race plan or the
  // rolling training journey (P3, surfaces audit §2.10). None → grey the row out.
  //
  // NOTE: this pass collapses the Preferences → /plans hop only. The settings
  // modal's OWN outbound hops (its "Edit fueling profile →" row back into
  // Preferences, its "View on calendar" navigate) are intentionally left alone —
  // the interlink triangle is not fully collapsed here.
  const { journeys } = useJourneys();
  // Soonest-first (useJourneys ordering). List every ACTIVE journey — race
  // plans AND the rolling training journey (which has NO drops until a box is
  // ordered, but `JourneySettingsModal` fully supports it: cadence + archive +
  // its own "Training journey settings" title). The old `drops.length > 0`
  // gate hid a live rolling journey from this row (Lane 5 §4).
  const activeJourneys = useMemo(
    () => journeys.filter((j) => j.status === "active"),
    [journeys]
  );
  const hasJourney = activeJourneys.length > 0;
  const openPlanSettings = useOpenPlanSettings();
  const { open: openModal, close: closeModal } = useModal();

  const reopenPreferences = useCallback(
    (view?: "fueling" | "taste") => {
      const id = openModal(
        <PreferencesModal
          onClose={() => closeModal(id)}
          onSaved={onSaved}
          onRegenerate={effectiveRegenerate ?? undefined}
          resumeView={view}
        />
      );
    },
    [openModal, closeModal, onSaved, effectiveRegenerate]
  );

  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon"
      ? authState.csrfToken
      : null;
  const isCustomer = authState.kind === "customer";
  const { distanceUnit } = useUnits();

  const [options, setOptions] = useState<PreferenceOptionCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state.
  const [fuelTypes, setFuelTypes] = useState<TriMap>({});
  const [flavors, setFlavors] = useState<TriMap>({});
  const [brands, setBrands] = useState<TriMap>({});
  const [dietaryNeeds, setDietaryNeeds] = useState<Set<string>>(new Set());
  // Dietary style (`dietaryPreferences`) is no longer editable in the UI, but we
  // carry any previously-saved value through untouched so a save never clears it.
  const savedDietaryPrefsRef = useRef<string[]>([]);
  const [caffeineAllowed, setCaffeineAllowed] = useState(true);
  const [caffeineMaxPct, setCaffeineMaxPct] = useState(50);
  /** Seconds per mile — saved immediately (like distance units), not via Taste Save. */
  const [runningPaceSecPerMi, setRunningPaceSecPerMi] = useState<number | null>(null);
  // Physiology lives on the `Customer` row — separate from CustomerPreferences.
  const [sex, setSex] = useState<Sex | null>(null);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("lb");
  // Athlete-level fueling (FuelingProfile) — server-side even for guests (the
  // engine reads it at generate time), so this section PATCHes the API for
  // both auth kinds rather than riding the anon localStorage draft.
  const fueling = useFuelingProfileForm();
  // 3-identity nav (F5): a small menu → Fueling profile / Taste, with Race plan
  // settings navigating out. The `fueling` deep-link opens that view directly.
  const [view, setView] = useState<"menu" | "fueling" | "taste">(
    resumeView ??
      (initialSection === "fueling" ? "fueling" : initialSection === "taste" ? "taste" : "menu")
  );

  const hydrate = useCallback(
    (prefs: SerializedPreferences | SavePreferencesInput | null, opts: PreferenceOptionCatalog) => {
      setOptions(opts);
      setFuelTypes(
        buildTriMap(
          opts.fuelTypes,
          prefs?.preferredProductTypes ?? [],
          prefs?.excludedProductTypes ?? []
        )
      );
      setFlavors(
        buildTriMap(
          opts.flavorProfiles,
          prefs?.preferredFlavorProfiles ?? [],
          prefs?.excludedFlavorProfiles ?? []
        )
      );
      setBrands(
        buildTriMap(opts.brands, prefs?.preferredBrands ?? [], prefs?.excludedBrands ?? [])
      );
      setDietaryNeeds(new Set(prefs?.dietaryNeeds ?? []));
      savedDietaryPrefsRef.current = prefs?.dietaryPreferences ?? [];
      setCaffeineAllowed(prefs?.caffeineAllowed ?? true);
      setCaffeineMaxPct(prefs?.caffeineMaxPercentOfFuel ?? 50);
      const serverPace =
        prefs && "averageRunningPaceSecondsPerMile" in prefs
          ? prefs.averageRunningPaceSecondsPerMile
          : null;
      const pace =
        serverPace != null && isValidRunningPaceSecPerMi(serverPace)
          ? serverPace
          : getAnonRunningPaceDraft();
      setRunningPaceSecPerMi(
        pace != null && isValidRunningPaceSecPerMi(pace) ? pace : null
      );
    },
    []
  );

  const hydratePhysiology = useCallback((profile: CustomerProfile | null) => {
    setSex(profile?.sex ?? null);
    setWeight(profile?.weight != null ? String(profile.weight) : "");
    setWeightUnit(profile?.weightUnit ?? "lb");
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    // Preferences (CustomerPreferences) and physiology (Customer) are separate
    // models — load both, but a physiology read failure must not block the
    // preferences UI, so it falls back to the anon/session draft.
    Promise.all([
      loadPreferences(ctrl.signal),
      loadProfile(ctrl.signal).catch(() => null)
    ])
      .then(([prefRes, profileRes]) => {
        if (ctrl.signal.aborted) return;
        hydrate(
          prefRes.preferences ?? getAnonPreferencesDraft<SavePreferencesInput>(),
          prefRes.options
        );
        const serverPhy = profileRes ?? emptyCustomerProfile();
        hydratePhysiology(
          isCustomer ? serverPhy : overlayAnonPhysiologyOntoLoaded(serverPhy)
        );
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        setError(err instanceof Error ? err.message : "Could not load preferences");
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [hydrate, hydratePhysiology, isCustomer]);

  const buildPayload = useCallback((): SavePreferencesInput => {
    const ft = splitTriMap(fuelTypes);
    const fl = splitTriMap(flavors);
    const br = splitTriMap(brands);
    return {
      dietaryNeeds: Array.from(dietaryNeeds),
      dietaryPreferences: savedDietaryPrefsRef.current,
      preferredProductTypes: ft.loved,
      excludedProductTypes: ft.avoided,
      preferredFlavorProfiles: fl.loved,
      excludedFlavorProfiles: fl.avoided,
      preferredBrands: br.loved,
      excludedBrands: br.avoided,
      caffeineAllowed,
      caffeineMaxMgPerHour: null,
      caffeineMaxPercentOfFuel: caffeineMaxPct
    };
  }, [fuelTypes, flavors, brands, dietaryNeeds, caffeineAllowed, caffeineMaxPct]);

  // Dirty tracking (plan-page regenerate split). A stable signature of every
  // editable value; the baseline is captured once after the initial hydrate, so
  // `dirty` flips the moment the customer changes anything (taste, physiology or
  // — via `fueling.dirty` — the fueling profile).
  const currentSig = useMemo(
    () =>
      JSON.stringify({
        prefs: buildPayload(),
        sex,
        weight,
        weightUnit,
        runningPaceSecPerMi
      }),
    [buildPayload, sex, weight, weightUnit, runningPaceSecPerMi]
  );
  const baselineSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!loading && !error && options && baselineSigRef.current === null) {
      baselineSigRef.current = currentSig;
    }
  }, [loading, error, options, currentSig]);
  const dirty =
    (baselineSigRef.current !== null && baselineSigRef.current !== currentSig) || fueling.dirty;

  const handleSave = useCallback(async (regenerateAfter = false) => {
    if (saving) return;
    const payload = buildPayload();
    const physiology = physiologyFromInputs({ sex, weightText: weight, weightUnit });

    // Athlete fueling (FuelingProfile) is SERVER-side for guests too — the
    // engine reads it at generate time, so it never rides the browser draft.
    const saveFueling = async (): Promise<boolean> => {
      if (!fueling.dirty || !csrfToken) return true;
      try {
        const view = await patchFuelingProfile(fueling.buildPatch(), csrfToken, refreshCsrf);
        fueling.applyView(view);
        return true;
      } catch (err) {
        const message =
          err instanceof ApiError && err.userSafeMessage
            ? err.userSafeMessage
            : err instanceof Error
              ? err.message
              : "Save failed";
        pushToast({ tone: "error", message: "Couldn't save your fueling profile", detail: message });
        return false;
      }
    };

    // Anonymous: no DB row for taste preferences — keep them + physiology in
    // the browsing session (§9); they attach to the account on sign-in. The
    // fueling profile still saves server-side (anon session row).
    const persistRunningPace = async (): Promise<boolean> => {
      if (
        runningPaceSecPerMi == null ||
        !isValidRunningPaceSecPerMi(runningPaceSecPerMi)
      ) {
        if (!isCustomer) setAnonRunningPaceDraft(null);
        return true;
      }
      if (!isCustomer) {
        setAnonRunningPaceDraft(runningPaceSecPerMi);
        return true;
      }
      if (!csrfToken) return false;
      try {
        await saveRunningPace(runningPaceSecPerMi, csrfToken, refreshCsrf);
        return true;
      } catch (err) {
        const message =
          err instanceof ApiError && err.userSafeMessage
            ? err.userSafeMessage
            : err instanceof Error
              ? err.message
              : "Save failed";
        pushToast({
          tone: "error",
          message: "Couldn't save running pace",
          detail: message
        });
        return false;
      }
    };

    if (!isCustomer) {
      setSaving(true);
      const fuelingOk = await saveFueling();
      // Slice E (preferences-outbound-alignment): also persist a server-side
      // copy on the anon session so read-time plan freshness can detect a prefs
      // edit (`preferences_changed`) on the guest's existing plans. Best-effort
      // — the localStorage draft below is the resilient store, so a failed
      // server write never blocks the save. Freshness-only; display still reads
      // the draft.
      if (csrfToken) {
        try {
          await savePreferences(payload, csrfToken, refreshCsrf);
        } catch {
          /* freshness-only; draft persists below */
        }
      }
      const paceOk = await persistRunningPace();
      setSaving(false);
      if (!fuelingOk || !paceOk) return; // keep the modal open so the save can be retried
      setAnonPreferencesDraft(payload);
      setAnonPhysiologySession(physiology);
      pushToast({
        tone: "success",
        message: "Preferences saved",
        detail: "Kept on this browser for up to 28 days. Sign in at checkout and we'll attach them to your account."
      });
      notifyPreferencesSaved();
      onSaved?.();
      onClose();
      if (regenerateAfter) effectiveRegenerate?.();
      return;
    }

    if (!csrfToken) return;
    setSaving(true);
    try {
      // Preferences, physiology and the fueling profile are separate models,
      // written via separate endpoints — save all before reporting success.
      const fuelingOk = await saveFueling();
      if (!fuelingOk) {
        setSaving(false);
        return;
      }
      const paceOk = await persistRunningPace();
      if (!paceOk) {
        setSaving(false);
        return;
      }
      const [res, profileRes] = await Promise.all([
        savePreferences(payload, csrfToken, refreshCsrf),
        saveProfile(physiology, csrfToken, refreshCsrf)
      ]);
      hydrate(res.preferences, res.options);
      hydratePhysiology(profileRes);
      pushToast({
        tone: "success",
        message: "Preferences saved",
        detail: "Your next plan will reflect these."
      });
      notifyPreferencesSaved();
      onSaved?.();
      onClose();
      if (regenerateAfter) effectiveRegenerate?.();
    } catch (err) {
      const message =
        err instanceof ApiError && err.userSafeMessage
          ? err.userSafeMessage
          : err instanceof Error
            ? err.message
            : "Save failed";
      pushToast({ tone: "error", message: "Couldn't save preferences", detail: message });
    } finally {
      setSaving(false);
    }
  }, [
    saving,
    buildPayload,
    sex,
    weight,
    weightUnit,
    runningPaceSecPerMi,
    fueling,
    isCustomer,
    csrfToken,
    refreshCsrf,
    hydrate,
    hydratePhysiology,
    pushToast,
    onSaved,
    onClose,
    effectiveRegenerate
  ]);

  const grid = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
      gap: 10
    }),
    [isMobile]
  );

  const toggleSet = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>) => (key: string) =>
    setFn((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const viewTitle =
    view === "fueling"
      ? "Fueling profile"
      : view === "taste"
        ? "Product preferences"
        : "Your preferences";
  const viewSubtitle =
    view === "fueling"
      ? "Physiology, gut training and targets — everything that sizes your fuel."
      : null;
  // Opened straight to a section (from the rail / wizard) → a focused sheet:
  // no back-to-menu chrome, close via ✕. Menu-entry sub-views keep the back.
  const isFocusedSheet = initialSection != null && resumeView == null;

  const guestBanner = !isCustomer ? (
    <div className="fgm-pref-guest">
      You&apos;re browsing as a guest — preferences are saved in this browser for up to 28 days and
      linked to your account when you sign in at checkout.
    </div>
  ) : null;

  return (
    <ModalShell
      eyebrow="Preferences"
      title={viewTitle}
      subtitle={viewSubtitle}
      scrollResetKey={view}
      onClose={onClose}
      onBack={view === "menu" || isFocusedSheet ? undefined : () => setView("menu")}
      width={view === "menu" ? 560 : 920}
      footer={
        view === "menu" ? (
          <div className="fgm-pref-footer-actions">
            <button type="button" className="fg-btn fg-btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <div className="fgm-pref-footer-actions">
            <button type="button" className="fg-btn fg-btn--ghost" onClick={() => setView("menu")}>
              Back
            </button>
            {effectiveRegenerate && dirty ? (
              <>
                <button
                  type="button"
                  className="fg-btn fg-btn--ghost"
                  onClick={() => void handleSave(false)}
                  disabled={saving || loading || !!error}>
                  {saving ? "Saving…" : "Save only"}
                </button>
                <button
                  type="button"
                  className="fg-btn fg-btn--primary"
                  onClick={() => void handleSave(true)}
                  disabled={saving || loading || !!error}>
                  {saving ? "Saving…" : "Save & regenerate"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="fg-btn fg-btn--primary"
                onClick={() => void handleSave()}
                disabled={saving || loading || !!error}>
                {saving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        )
      }>
      <div>
        {loading ? (
          <div className="fgm-pref-state--loading">
            <Spinner accessibilityLabel="Loading your preferences" />
          </div>
        ) : error || !options ? (
          <div className="fgm-pref-state">{error ?? "Couldn't load preferences"}</div>
        ) : view === "menu" ? (
          <div className="fgm-pref-menu">
            {guestBanner}
            <p className="fgm-pref-menu__rule">
              Your <strong>Fueling profile</strong> and product preferences are shared by every race
              plan. 
            </p>
            <button type="button" className="fgm-pref-menu__row" onClick={() => setView("fueling")}>
              <span className="fgm-pref-menu__icon" aria-hidden>
                ⚡
              </span>
              <span className="fgm-pref-menu__text">
                <span className="fgm-pref-menu__t">Fueling profile</span>
                <span className="fgm-pref-menu__b">
                  Your body, carb targets &amp; dietary requirements
                </span>
              </span>
              <span className="fgm-pref-menu__chev" aria-hidden>
                ›
              </span>
            </button>
            <button type="button" className="fgm-pref-menu__row" onClick={() => setView("taste")}>
              <span className="fgm-pref-menu__icon" aria-hidden>
                🎨
              </span>
              <span className="fgm-pref-menu__text">
                <span className="fgm-pref-menu__t">Product preferences</span>
                <span className="fgm-pref-menu__b">
                  Flavors, fuel types, brands &amp; caffeine
                </span>
              </span>
              <span className="fgm-pref-menu__chev" aria-hidden>
                ›
              </span>
            </button>
            {!hasJourney ? (
              // No journey → the row stays disabled exactly as before (nothing
              // to open); prompts starting a plan.
              <button type="button" className="fgm-pref-menu__row" disabled>
                <span className="fgm-pref-menu__icon" aria-hidden>
                  🏁
                </span>
                <span className="fgm-pref-menu__text">
                  <span className="fgm-pref-menu__t">Race & plan settings</span>
                  <span className="fgm-pref-menu__b">
                    Start a race plan or training journey to set goal &amp; box schedule
                  </span>
                </span>
              </button>
            ) : activeJourneys.length === 1 ? (
              // Single journey → open its settings modal directly (no /plans hop).
              <button
                type="button"
                className="fgm-pref-menu__row"
                onClick={() => {
                  onClose();
                  openPlanSettings(activeJourneys[0], () => reopenPreferences());
                }}>
                <span className="fgm-pref-menu__icon" aria-hidden>
                  🏁
                </span>
                <span className="fgm-pref-menu__text">
                  <span className="fgm-pref-menu__t">
                  {activeJourneys[0].journeyType === "rolling"
                    ? "Training journey settings"
                    : "Race & plan settings"}
                </span>
                  <span className="fgm-pref-menu__b">
                    Goal, box schedule &amp; race-day target — per journey
                  </span>
                </span>
                <span className="fgm-pref-menu__chev" aria-hidden>
                  ›
                </span>
              </button>
            ) : (
              // Multiple journeys → one row per journey, labeled by race name;
              // each opens that journey's settings directly.
              activeJourneys.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  className="fgm-pref-menu__row"
                  onClick={() => {
                    onClose();
                    openPlanSettings(j, () => reopenPreferences());
                  }}>
                  <span className="fgm-pref-menu__icon" aria-hidden>
                    🏁
                  </span>
                  <span className="fgm-pref-menu__text">
                    <span className="fgm-pref-menu__t">{journeyRaceName(j)} settings</span>
                    <span className="fgm-pref-menu__b">
                      Goal, box schedule &amp; race-day target
                    </span>
                  </span>
                  <span className="fgm-pref-menu__chev" aria-hidden>
                    ›
                  </span>
                </button>
              ))
            )}
            <div className="fgm-pref-menu__units">
              <span className="fgm-pref-menu__units-label">Distance units</span>
              <UnitToggle ariaLabel="Distance units" />
            </div>
          </div>
        ) : view === "fueling" ? (
          <div className="fgm-pref-sections">
            {guestBanner}
            <TunePhysiologyCard
              sex={sex}
              weight={weight}
              weightUnit={weightUnit}
              onSexChange={setSex}
              onWeightChange={setWeight}
              onWeightUnitChange={setWeightUnit}
              disabled={saving}
            />
            <Section
              title="Fueling"
              hint="Sweat, gut and everyday carb targets — one profile, shared by every race plan and generated box.">
              <FuelingSection
                form={fueling}
                disabled={saving}
                // From inside Fueling, "which journey" is ambiguous with many
                // plans, so route to the soonest journey's settings (list is
                // soonest-first) — the simplest coherent behaviour. No journey →
                // omit the link entirely.
                onOpenPlanSettings={
                  hasJourney
                    ? () => {
                        onClose();
                        openPlanSettings(activeJourneys[0], () => reopenPreferences("fueling"));
                      }
                    : undefined
                }
              />
            </Section>
            <Section
              title="Allergens & must-avoids"
              titleId="fgm-pref-allergens-title"
              hint="Must-avoids are locked in — we never cross these lines.">
              <ChipRow
                options={options.dietaryNeeds}
                selected={dietaryNeeds}
                onToggle={toggleSet(setDietaryNeeds)}
                tone="purple"
                ariaLabelledby="fgm-pref-allergens-title"
              />
            </Section>
            <Section title="Average running pace" hint={RUNNING_PACE_HINT_PREFERENCES}>
              <RunningPaceField
                valueSecPerMi={runningPaceSecPerMi}
                unit={distanceUnit}
                disabled={saving}
                hideLabel
                onValidDraft={(sec) => {
                  if (sec != null) setRunningPaceSecPerMi(sec);
                }}
                onCommit={(secPerMi) => {
                  setRunningPaceSecPerMi(secPerMi);
                }}
              />
            </Section>
          </div>
        ) : (
          <div className="fgm-pref-sections">
            <Section
              title="Flavors"
              hint="Life's too short for gels you hate. Tell us what you love or what you don't.">
              <div style={grid}>
                {options.flavorProfiles.map((o) => (
                  <PrefCard
                    key={o.key}
                    label={o.label}
                    hint={o.hint}
                    value={flavors[o.key] ?? "neutral"}
                    onChange={(v) => setFlavors((m) => ({ ...m, [o.key]: v }))}
                  />
                ))}
              </div>
            </Section>
            <Section
              title="Fuel types"
              hint="Bars, gels, real food — everyone has a preference. Pick what you'll use and anything you'd rather skip.">
              <div style={grid}>
                {options.fuelTypes
                  .filter((o) => !UI_HIDDEN_FUEL_TYPE_KEYS.has(o.key))
                  .map((o) => (
                    <PrefCard
                      key={o.key}
                      label={o.label}
                      hint={o.hint}
                      value={fuelTypes[o.key] ?? "neutral"}
                      onChange={(v) => setFuelTypes((m) => ({ ...m, [o.key]: v }))}
                    />
                  ))}
              </div>
            </Section>
            <Section
              title="Brands"
              hint="Got a brand you swear by? Or one you've sworn off? Tell us and we'll plan around it.">
              <div className="fgm-pref-brands-grid">
                {options.brands.map((o) => (
                  <PrefCard
                    key={o.key}
                    label={o.label}
                    hint={o.hint}
                    value={brands[o.key] ?? "neutral"}
                    onChange={(v) => setBrands((m) => ({ ...m, [o.key]: v }))}
                  />
                ))}
              </div>
            </Section>
            <Section
              title="Caffeine"
              hint="Some people live for the caffeine kick. We'll make sure your plan reflects your limits, not ours.">
              <div className="fgm-pref-caffeine__grid">
                <span
                  id={caffeineProductsLabelId}
                  className="fgm-pref-caffeine__lab-products fgm-pref-caffeine__field-label">
                  Caffeinated products in your plan
                </span>
                {caffeineAllowed && (
                  <span
                    id={caffeineCapLabelId}
                    className="fgm-pref-caffeine__lab-cap fgm-pref-caffeine__field-label">
                    Caffeinated product cap
                  </span>
                )}
                <div className="fgm-pref-caffeine__controls-seg">
                  <SegmentedControl
                    ariaLabelledby={caffeineProductsLabelId}
                    options={[
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" }
                    ]}
                    value={caffeineAllowed ? "yes" : "no"}
                    onChange={(v) => setCaffeineAllowed(v === "yes")}
                  />
                </div>
                <div className="fgm-pref-caffeine__controls-slider">
                  <CaffeineSliderStack
                    allowed={caffeineAllowed}
                    capLabelId={caffeineCapLabelId}
                    value={caffeineMaxPct}
                    reserveSlotWhenHidden={caffeineRowWide && device !== "mobile"}
                    onValueChange={setCaffeineMaxPct}
                  />
                </div>
              </div>
            </Section>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function CaffeineSliderStack({
  allowed,
  value,
  reserveSlotWhenHidden,
  capLabelId,
  onValueChange
}: {
  allowed: boolean;
  value: number;
  capLabelId: string;
  /** Keeps slider column mounted + layout size when toggling Yes/No (tablet/desktop grid). */
  reserveSlotWhenHidden: boolean;
  onValueChange: (n: number) => void;
}) {
  const body = (
    <div className="fgo-slider fgm-pref-caffeine__slider">
      <input
        id="fgm-pref-caffeine-cap"
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        disabled={!allowed}
        aria-labelledby={capLabelId}
        onChange={(e) => onValueChange(Number(e.target.value))}
        className="fgo-slider__input"
      />
      <div className="fgo-slider__readout">{value}%</div>
    </div>
  );

  if (reserveSlotWhenHidden) {
    return (
      <div
        className={
          allowed
            ? "fgm-pref-caffeine__slider-stack"
            : "fgm-pref-caffeine__slider-stack fgm-pref-caffeine__slider-stack--concealed"
        }
        aria-hidden={!allowed}>
        {body}
      </div>
    );
  }
  if (!allowed) return null;
  return <div className="fgm-pref-caffeine__slider-stack">{body}</div>;
}

function Section({
  title,
  hint,
  titleId,
  children
}: {
  title: string;
  hint?: string;
  /** Optional id on the heading so a grouped control can `aria-labelledby` it. */
  titleId?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 id={titleId} className="fgm-pref-section__title">
        {title}
      </h3>
      {hint && (
        <p className="fgm-pref-section__hint">
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

/** Exported for the wizard's "About you" step (allergen chips) — same UI, one source. */
export function ChipRow({
  options,
  selected,
  onToggle,
  tone,
  ariaLabelledby
}: {
  options: PreferenceOption[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  tone: "purple" | "lime";
  /** id of the visible heading naming this multi-select group. */
  ariaLabelledby?: string;
}) {
  if (options.length === 0) {
    return (
      <p className="fgm-pref-chiprow-empty">
        Nothing to set here yet.
      </p>
    );
  }
  return (
    <div className="fgm-pref-chiprow" role="group" aria-labelledby={ariaLabelledby}>
      {options.map((o) => {
        const on = selected.has(o.key);
        const onBg = tone === "purple" ? "var(--fg-purple)" : "var(--fg-lime-deep)";
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o.key)}
            title={o.hint}
            style={{
              appearance: "none",
              cursor: "pointer",
              padding: "8px 14px",
              borderRadius: 'var(--fg-r-pill)',
              border: on ? `1px solid ${onBg}` : "1px solid var(--fg-gray-300)",
              background: on ? onBg : "#fff",
              color: on ? "#fff" : "var(--fg-ink)",
              fontFamily: "var(--fg-body)",
              fontWeight: 500,
              fontSize: 13
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
